"use client";

import { Container, Typography, Box, Paper, Button, useTheme } from "@mui/material";
import EnrollmentHeader from "@/components/EnrollmentHeader";
import EnrollmentFooter from "@/components/EnrollmentFooter";
import { usePageTitle } from "@/hooks/usePageTitle";
import { resolveStepDisabled, useStudyStatus } from "@/hooks/useStudyStatus";

export default function CadaFrPortalPage() {
    const theme = useTheme();
    const tenant = "cada";

    usePageTitle("Portail", tenant);

    const { stepOverrides } = useStudyStatus(tenant);
    const enrollDisabled = resolveStepDisabled(stepOverrides.enroll, false);
    const editParticipantsDisabled = resolveStepDisabled(stepOverrides.edit_participants, false);
    const surveyDisabled = resolveStepDisabled(stepOverrides.survey, false);
    const uploadPayrollDisabled = resolveStepDisabled(stepOverrides.upload_payroll, false);

    return (
        <Box sx={{ bgcolor: "white", minHeight: "100vh" }}>
            <EnrollmentHeader tenant={tenant} />

            {/* Main content */}
            <Container maxWidth="lg" sx={{ py: 4 }} component="main" role="main">
                <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
                    Portail des concessionnaires
                </Typography>

                {/* Step 1: Enroll */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step1-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step1-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Étape 1: S&rsquo;inscrire
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Trouvez votre concession avec votre numéro
                                        d&rsquo;identification de la CADA.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Confirmez que l&rsquo;information sur votre concession est
                                        exacte et acceptez les modalités de l&rsquo;accord avec la
                                        CADA.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Vous recevrez un courriel de confirmation.
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/enrollment"
                                aria-label={enrollDisabled ? "L'inscription est fermée" : "Aller à la page d'inscription"}
                                disabled={enrollDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                M&rsquo;inscrire maintenant
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 2: Confirm Your Participating Dealership Info */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step2-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step2-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Étape 2: Confirmer l&rsquo;information de participation des
                                concessions
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2">
                                        Modifiez ou complétez l&rsquo;information relative à la
                                        participation de vos concessions et franchises.
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/edit-participants"
                                aria-label={editParticipantsDisabled ? "La modification des participants est fermée" : "Aller à la page de modification des participants"}
                                disabled={editParticipantsDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Modifier les participants
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 3: The Questionnaire */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step3-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step3-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Étape 3: Remplir le questionnaire
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Entrez dans le questionnaire en ligne les détails de vos
                                        régimes d&rsquo;avantages sociaux et de rémunération.
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/survey"
                                aria-label={surveyDisabled ? "Le sondage est fermé" : "Aller à la page du sondage"}
                                disabled={surveyDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Accéder au sondage
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 4: Upload Your Payroll File */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step4-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step4-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Étape 4: Téléverser le fichier de données salariales
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Téléchargez le modèle de registre de paie.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Extrayez les données de votre système de paie.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Remplissez le modèle de registre de paie avec les données de
                                        TOUS vos employés.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Incluez tous les employés inscrits dans votre système de
                                        paie en date du 31 décembre 2025.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Téléversez votre fichier de données salariales complet.
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/upload"
                                aria-label={uploadPayrollDisabled ? "Le téléchargement est fermé" : "Aller à la page de téléchargement"}
                                disabled={uploadPayrollDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Télécharger les données
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Closing message */}
                <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, textAlign: "center", my: 4 }}
                    role="alert"
                    aria-live="polite"
                >
                    Toutes les données doivent être soumises avant le 30 mars 2026.
                </Typography>

                <EnrollmentFooter tenant={tenant} />
            </Container>
        </Box>
    );
}
